import { useEffect } from 'react'
import { dropdownLayerOpen } from '../../features/taskManagement'

/** Rand ums Fenster für den Schatten der Karte — 12px oben, 44px unten (`QuickAddWindow.scss`). */
const SHADOW_MARGIN = 56

/**
 * Platz unter der Karte für einen offenen Kasten — Wann, Tags, das Bereichsmenü. Die Kästen schweben
 * außerhalb der Karte und würden am Fensterrand abgeschnitten. Mindestens so viel; ein höherer Kasten
 * bekommt seine Höhe (Bild 42, 04.10.2026: „Wann“ ging sonst rechts auf).
 */
const OVERLAY_ROOM = 420

/** Unter einem Kasten: 4 Abstand zum Auslöser, 52 für die Leiste (`reserveBottom`), 8 Rand (`TaskDropdown`). */
const DROPDOWN_ALLOWANCE = 64

/** Die Kästen von `TaskDropdown` — die Radix-Menüs (Ziel) weichen selbst nach oben aus. */
const TASK_DROPDOWN = '[data-task-dropdown]'

/** So lange fährt die Karte ihre Höhe (`heightMotion`, 0,26 s) — erst danach wird das Fenster kleiner. */
const SHRINK_DELAY_MS = 300

/**
 * **Das Fenster folgt der Karte** (#234, F2): Es wächst sofort — die Karte fährt danach in den Platz
 * hinein — und schrumpft erst, wenn ihre Bewegung durch ist. Sonst schnitte das Fenster die Karte
 * mitten im Zufahren ab.
 *
 * Die Oberkante setzt der Hauptprozess. Neben der Höhe geht der echte Bedarf mit — Karte, Schattenrand,
 * offener Kasten samt Abstand, ohne die pauschale Reserve: Passt der nicht unter die Karte, rückt der
 * Hauptprozess sie hoch (#275). Nach der Reserve gerückt, zuckte sie am oberen Drittel eines MacBooks, obwohl
 * kein Kasten anstößt.
 */
export function useQuickAddWindowHeight(cardRef: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const card = cardRef.current
    const resize = window.windowAPI?.resizeQuickAdd
    if (!card || !resize) return

    let reported = { height: 0, need: 0 }
    let shrinkTimer: ReturnType<typeof setTimeout> | undefined
    // Die Höhe geht bei jeder Bewegung raus. Fehlt der Kanal (Hauptprozess nach einem Hot Reload noch
    // alt), reicht EINE Warnung — sonst flutet jede Messung die Fehleranzeige.
    let warned = false
    const send = (next: { height: number; need: number }) => {
      reported = next
      resize(next.height, next.need).catch((error: unknown) => {
        if (warned) return
        warned = true
        console.warn('[QuickAdd] Höhe nicht gemeldet:', error)
      })
    }

    const sizeObserver = new ResizeObserver(() => report())
    const report = () => {
      const cardHeight = card.getBoundingClientRect().height
      // Eine Null misst ein verstecktes Fenster, keine Karte.
      if (cardHeight <= 0) return
      const dropdowns = [...document.querySelectorAll<HTMLElement>(TASK_DROPDOWN)]
      // Ein Kasten, der den Inhalt wechselt (Kalender, Bestätigen), wächst danach noch.
      for (const dropdown of dropdowns) sizeObserver.observe(dropdown)
      const tallest = Math.max(0, ...dropdowns.map(dropdown => dropdown.getBoundingClientRect().height))
      const layerOpen = dropdownLayerOpen()
      const own = Math.ceil(cardHeight) + SHADOW_MARGIN
      const box = tallest > 0 ? Math.ceil(tallest) + DROPDOWN_ALLOWANCE : 0
      const target = {
        height: own + (layerOpen ? Math.max(OVERLAY_ROOM, box) : 0),
        need: own + (layerOpen ? box : 0),
      }
      clearTimeout(shrinkTimer)
      if (target.height > reported.height || target.need > reported.need) {
        send(target)
      } else if (target.height < reported.height || target.need < reported.need) {
        shrinkTimer = setTimeout(() => send(target), SHRINK_DELAY_MS)
      }
    }

    sizeObserver.observe(card)
    // Kästen hängen sich als Portal an den `body` — ihr Kommen und Gehen ändert die nötige Höhe.
    const layerObserver = new MutationObserver(report)
    layerObserver.observe(document.body, { childList: true })
    report()

    return () => {
      sizeObserver.disconnect()
      layerObserver.disconnect()
      clearTimeout(shrinkTimer)
    }
  }, [cardRef])
}
